/**
 * Single checklist item: collapsible card with title, content (HTML), rating buttons (0–3),
 * and optional syntax-highlighted code blocks.
 */
import React, { useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import hljs from 'highlight.js/lib/core';
import json from 'highlight.js/lib/languages/json';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import yaml from 'highlight.js/lib/languages/yaml';
import markdown from 'highlight.js/lib/languages/markdown';

import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import type { ChecklistItem } from '../types';
import { getRatingOptions } from '../utils/ratingLabels';
import { sanitizeHtml, isHtmlSafe } from '../utils/sanitize';
import { cn } from '@/lib/utils';

// Register languages for syntax highlighting
hljs.registerLanguage('json', json);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('ts', typescript);
hljs.registerLanguage('tsx', typescript);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('yaml', yaml);
hljs.registerLanguage('yml', yaml);
hljs.registerLanguage('markdown', markdown);
hljs.registerLanguage('md', markdown);

interface ChecklistItemRatingProps {
  item: ChecklistItem;
  rating: number | null;
  expanded: boolean;
  onRate: (itemId: string, rating: number) => void;
  onToggleExpand: (itemId: string) => void;
  allItemsMap: Map<number, { id: string; title: string }>;
}

const difficultyConfig = {
  quick: { variant: 'success' as const, label: 'Quick win' },
  medium: { variant: 'warning' as const, label: 'Medium effort' },
  significant: { variant: 'danger' as const, label: 'Significant investment' },
};

const ChecklistItemRatingComponent: React.FC<ChecklistItemRatingProps> = ({
  item,
  rating,
  expanded,
  onRate,
  onToggleExpand,
  allItemsMap,
}) => {
  const config = difficultyConfig[item.difficulty] ?? difficultyConfig.medium;
  const ratingOptions = getRatingOptions(item.number);
  const contentRef = useRef<HTMLDivElement>(null);

  // Sanitize HTML content for XSS protection (defense in depth)
  const sanitizedContent = useMemo(() => {
    if (!item.content || typeof item.content !== 'string') {
      return '';
    }
    // Quick check first, then full sanitization if needed
    if (isHtmlSafe(item.content)) {
      return item.content;
    }
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Potentially unsafe HTML detected in item ${item.id}, sanitizing...`);
    }
    return sanitizeHtml(item.content);
  }, [item.content, item.id]);

  // Apply syntax highlighting when content is expanded
  useEffect(() => {
    if (expanded && contentRef.current) {
      const codeBlocks = contentRef.current.querySelectorAll('pre code:not(.hljs)');
      codeBlocks.forEach((block) => {
        hljs.highlightElement(block as HTMLElement);
      });
    }
  }, [expanded, sanitizedContent]);

  const isAnswered = rating !== null && rating !== undefined && typeof rating === 'number';

  // Memoize handlers to prevent unnecessary re-renders
  const handleRate = useCallback((value: number) => {
    onRate(item.id, value);
  }, [onRate, item.id]);

  const handleToggle = useCallback(() => {
    onToggleExpand(item.id);
  }, [onToggleExpand, item.id]);

  return (
    <Card
      id={item.id}
      className={cn(
        'mb-4 transition-all duration-200 rounded-lg scroll-mt-24',
        isAnswered
          ? 'bg-slate-50/50 border-border/60'
          : 'bg-white hover:shadow-md hover:border-border'
      )}
      role="listitem"
    >
      <Collapsible open={expanded} onOpenChange={handleToggle}>
        <div className="p-5">
          <CollapsibleTrigger
            className="w-full text-left group"
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.title}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3
                  id={`${item.id}-title`}
                  className="text-[16px] font-medium text-text-primary group-hover:text-primary transition-colors leading-snug mb-2"
                >
                  {item.number}. {item.title}
                </h3>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={config.variant} aria-label={`Difficulty: ${config.label}`}>
                    {config.label}
                  </Badge>
                  {item.impact && (
                    <Badge variant="secondary" aria-label={`Impact: ${item.impact}`}>
                      {item.impact}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isAnswered && (
                  <Badge
                    className="bg-transparent text-blue-700 hover:bg-transparent border-transparent"
                    aria-label="Question answered"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Answered
                  </Badge>
                )}
                <div className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center transition-colors',
                  'group-hover:bg-primary/10'
                )}>
                  <ChevronDown
                    className={cn(
                      'w-5 h-5 text-text-secondary flex-shrink-0 transition-transform duration-200',
                      'group-hover:text-primary',
                      expanded && 'rotate-180'
                    )}
                    aria-hidden
                  />
                </div>
              </div>
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
            <div className="mt-5">
              {sanitizedContent && (
                <div
                  ref={contentRef}
                  className="prose max-w-none [&>:first-child]:mt-0 [&>:last-child]:mb-0"
                  dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                />
              )}

              <div className="mt-10" role="group" aria-label="Rating options">
                <hr className="border-t border-border mb-6" />
                <h4 className="text-sm font-medium text-text-primary text-center mb-4">
                  How does your system measure up?
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {ratingOptions.map((option) => {
                    const isSelected =
                      rating !== null &&
                      rating !== undefined &&
                      typeof rating === 'number' &&
                      rating === option.value;
                    return (
                      <Button
                        key={option.value}
                        onClick={() => handleRate(option.value)}
                        variant={isSelected ? 'default' : 'outline'}
                        size="default"
                        className={cn(
                          'h-auto min-h-[3.5rem] w-full py-4 px-4 flex items-center justify-center text-center transition-all duration-200',
                          !isSelected && 'hover:border-primary hover:shadow-sm'
                        )}
                        aria-label={`Rate as ${option.label}`}
                        aria-pressed={isSelected}
                      >
                        <span className="text-sm font-medium text-center">
                          {option.label}
                        </span>
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </Card>
  );
};

// Memoize to prevent unnecessary re-renders when parent state changes
export const ChecklistItemRating = memo(ChecklistItemRatingComponent);
